/**
 * ProfileForm Component
 * 
 * Form for editing user profile with validation and immutability enforcement.
 * 
 * Features:
 * - All 8 required profile fields
 * - US States dropdown (50 states + DC)
 * - Date picker for DOB
 * - Client-side validation matching backend rules
 * - Lock indicators for name and DOB
 * - Real-time validation feedback
 * - Submit button with loading state
 * 
 * Created: 2025-01-17 (Phase 4.8: User Profile Management)
 * Phase: Phase 4.8 - Frontend Implementation
 */

import React, { useState, useEffect } from 'react';

// US States list (50 states + DC)
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'Washington, D.C.' }
];

const ProfileForm = ({ profile, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    phone: '',
    date_of_birth: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States'
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Initialize form with profile data
  useEffect(() => {
    if (profile) {
      setFormData({
        phone: profile.phone || '',
        date_of_birth: profile.date_of_birth || '',
        address_line_1: profile.address_line_1 || '',
        address_line_2: profile.address_line_2 || '',
        city: profile.city || '',
        state: profile.state || '',
        zip_code: profile.zip_code || '',
        country: profile.country || 'United States'
      });
    }
  }, [profile]);

  // Validation functions (matching backend rules)
  const validateField = (name, value) => {
    switch (name) {
      case 'date_of_birth':
        if (value) {
          try {
            const dob = new Date(value);
            const today = new Date();
            const age = today.getFullYear() - dob.getFullYear() - 
              ((today.getMonth() < dob.getMonth() || 
                (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) ? 1 : 0);
            
            if (age < 18) {
              return 'You must be at least 18 years old';
            }
            if (age > 120) {
              return 'Invalid date of birth';
            }
            if (dob > today) {
              return 'Date of birth cannot be in the future';
            }
          } catch (e) {
            return 'Invalid date format';
          }
        }
        break;

      case 'phone':
        if (value) {
          const digits = value.replace(/\D/g, '');
          if (digits.length !== 10) {
            return 'Phone number must be 10 digits';
          }
        }
        break;

      case 'zip_code':
        if (value) {
          if (!/^\d{5}$/.test(value)) {
            return 'ZIP code must be 5 digits';
          }
        }
        break;

      case 'state':
        if (value) {
          const validStates = US_STATES.map(s => s.code);
          if (!validStates.includes(value)) {
            return 'Invalid US state code';
          }
        }
        break;

      case 'address_line_1':
        if (value) {
          if (value.length < 5) {
            return 'Address must be at least 5 characters';
          }
          if (value.length > 100) {
            return 'Address must be less than 100 characters';
          }
        }
        break;

      case 'city':
        if (value) {
          if (value.length < 2) {
            return 'City name must be at least 2 characters';
          }
          if (value.length > 50) {
            return 'City name must be less than 50 characters';
          }
        }
        break;

      default:
        break;
    }
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validate on change if field was touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Validate on blur
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });

    setErrors(newErrors);
    setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));

    // If no errors, submit
    if (Object.keys(newErrors).length === 0) {
      // Only send fields that have values (backend allows partial updates)
      const updateData = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '' && formData[key] !== null) {
          updateData[key] = formData[key];
        }
      });
      
      await onSave(updateData);
    }
  };

  const isNameLocked = profile?.is_name_locked;
  const isDobLocked = profile?.is_dob_locked;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Personal Information Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* First Name (Locked) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name
            </label>
            <div className="relative">
              <input
                type="text"
                value={profile?.first_name || ''}
                disabled
                className="w-full px-4 py-3 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">🔒 Locked - Set at signup</p>
          </div>

          {/* Last Name (Locked) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name
            </label>
            <div className="relative">
              <input
                type="text"
                value={profile?.last_name || ''}
                disabled
                className="w-full px-4 py-3 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">🔒 Locked - Set at signup</p>
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="4155551234"
              className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.phone && touched.phone ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.phone && touched.phone && (
              <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">10 digits, no formatting</p>
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isDobLocked}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDobLocked ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
                } ${
                  errors.date_of_birth && touched.date_of_birth ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {isDobLocked && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              )}
            </div>
            {errors.date_of_birth && touched.date_of_birth && (
              <p className="text-xs text-red-500 mt-1">{errors.date_of_birth}</p>
            )}
            {isDobLocked ? (
              <p className="text-xs text-gray-500 mt-1">🔒 Locked - Cannot be changed</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">Must be 18+ years old</p>
            )}
          </div>
        </div>
      </div>

      {/* Address Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Address</h3>
        
        <div className="space-y-4">
          {/* Address Line 1 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address Line 1 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="address_line_1"
              value={formData.address_line_1}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="456 Market Street"
              className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.address_line_1 && touched.address_line_1 ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.address_line_1 && touched.address_line_1 && (
              <p className="text-xs text-red-500 mt-1">{errors.address_line_1}</p>
            )}
          </div>

          {/* Address Line 2 (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address Line 2 <span className="text-gray-400">(Optional)</span>
            </label>
            <input
              type="text"
              name="address_line_2"
              value={formData.address_line_2}
              onChange={handleChange}
              placeholder="Apt 301, Suite 100, etc."
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* City, State, ZIP Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="San Francisco"
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.city && touched.city ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.city && touched.city && (
                <p className="text-xs text-red-500 mt-1">{errors.city}</p>
              )}
            </div>

            {/* State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State <span className="text-red-500">*</span>
              </label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.state && touched.state ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select State</option>
                {US_STATES.map(state => (
                  <option key={state.code} value={state.code}>
                    {state.name}
                  </option>
                ))}
              </select>
              {errors.state && touched.state && (
                <p className="text-xs text-red-500 mt-1">{errors.state}</p>
              )}
            </div>

            {/* ZIP Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ZIP Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="94103"
                maxLength="5"
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.zip_code && touched.zip_code ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.zip_code && touched.zip_code && (
                <p className="text-xs text-red-500 mt-1">{errors.zip_code}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">5 digits only</p>
            </div>
          </div>

          {/* Country (Read-only for now) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              disabled
              className="w-full px-4 py-3 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">Currently US addresses only</p>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {loading ? 'Saving...' : 'Save Profile'}
        </button>
      </div>

      {/* Required Fields Notice */}
      <p className="text-xs text-gray-500 text-center">
        <span className="text-red-500">*</span> Required fields for profile completion
      </p>
    </form>
  );
};

export default ProfileForm;
